import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getComplaintDetail } from '@src/services/api';
import PageHeader from '@src/mobile/components/PageHeader';
import SharePanel from '@src/mobile/components/SharePanel';
import NavBar from '@src/mobile/components/NavBar';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';

const formatTime = (str) => {
    if (!str) return '';
    const d = new Date(str);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
        2,
        '0'
    )}`;
};

const getCmpIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('cmp_id');
};
const DETAIL_SHARE_TITLE_FALLBACK = '凤凰投诉';
const DETAIL_SHARE_DESC_FALLBACK = '聚焦金融&股市投资者心声，查看真实投诉详情。';

const buildDetailShareTitle = (detail) => {
    const target = detail?.cmp_target ? `【${detail.cmp_target}】` : '';
    const issue = detail?.cmp_issue || DETAIL_SHARE_TITLE_FALLBACK;
    return `${target}${issue}`;
};

const buildDetailShareDesc = (detail) => {
    const raw = detail?.cmp_detail || DETAIL_SHARE_DESC_FALLBACK;
    return raw.length > 60 ? `${raw.slice(0, 60)}...` : raw;
};

const Layout = ({ content }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewImage, setPreviewImage] = useState('');
    const cmpId = getCmpIdFromUrl();

    useEffect(() => {
        if (!cmpId) {
            setLoading(false);
            return;
        }
        const fetchDetail = async () => {
            try {
                const res = await getComplaintDetail(cmpId);
                const data = res?.data || res;
                setDetail(data);
            } catch (err) {
                setError(err.message || '加载失败');
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [cmpId]);

    if (loading) {
        return (
            <div className={styles.layout}>
                <div className={styles.loadingWrap}>
                    <div className={styles.loadingSpinner} />
                    <p className={styles.loadingText}>加载中...</p>
                </div>
            </div>
        );
    }

    if (!cmpId) {
        return (
            <div className={styles.layout}>
                <div className={styles.empty}>缺少投诉ID</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.layout}>
                <div className={styles.empty}>{error}</div>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className={styles.layout}>
                <div className={styles.empty}>暂无详情数据</div>
            </div>
        );
    }

    const images = detail.images || [];
    const isImageHidden = detail.image_hidden === 1;
    const showImages = images.length > 0;

    const infoRows = [
        { label: '投诉对象', value: detail.cmp_target },
        { label: '诉求问题', value: detail.cmp_issue },
        { label: '投诉详情', value: detail.cmp_detail },
        { label: '投诉时间', value: detail.created_at },
    ];
    const shareTitle = buildDetailShareTitle(detail);
    const shareDesc = buildDetailShareDesc(detail);
    const closePreview = () => setPreviewImage('');

    return (
        <div className={styles.layout}>
            <SharePanel title={shareTitle} desc={shareDesc} url={window.location.href} />
            <PageHeader showFormEntry desc="聚焦金融&股市投资者心声，等你来吐槽/点赞！" variant="banner" />
            <NavBar />
            <main className={styles.main}>
                <section className={styles.section}>
                    <div className={styles.userRow}>
                        <span className={styles.userLabel}>昵称</span>
                        <span className={styles.userName}>{detail.nickname}</span>
                    </div>
                </section>

                <section className={styles.section}>
                    {infoRows.map((row, i) => (
                        <div key={i} className={styles.infoRow}>
                            <span className={styles.infoLabel}>{row.label}</span>
                            <span className={styles.infoValue}>{row.value}</span>
                        </div>
                    ))}
                </section>

                {showImages && (
                    <section className={styles.section}>
                        <div className={styles.sectionTitle}>投诉图片</div>
                        <div className={styles.imageList}>
                            {isImageHidden ? (
                                <div className={styles.hiddenPlaceholder}>
                                    <span className={styles.hiddenIcon}>🔒</span>
                                    <span className={styles.hiddenText}>图片已隐藏</span>
                                </div>
                            ) : (
                                images.map((img, i) => (
                                    <div key={i} className={styles.imageItem}>
                                        <img src={img} alt={`投诉图片${i + 1}`} className={styles.image} onClick={() => setPreviewImage(img)} />
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                )}
            </main>
            {previewImage && (
                <div className={styles.previewMask} onClick={closePreview}>
                    <img src="https://x0.ifengimg.com/ucms/2026_10/25C22DC179B9A3EEDD2276930D7B71C4F7C5431E_size2_w128_h128.png" alt="关闭" className={styles.previewClose} onClick={closePreview} />
                    <img
                        src={previewImage}
                        alt="投诉大图预览"
                        className={styles.previewImage}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

Layout.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Layout);
